import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FOCUS_DISABLED_CLASS } from '../../focus-management';
import { ChevronUp, ChevronRight, ChevronDown } from '../../icons';
import { useIsInBottomSheet } from '../../providers';
import { useDevice } from '../../providers/Device';
import { useIntentBehavior } from '../../providers/IntentBehavior';
import { BottomSheetContentLayout } from '../BottomSheet';
import { Card } from '../Card';
import { Checkbox } from '../Checkbox';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Divider } from '../Divider';
import { IconButton } from '../IconButton';
import { FullRow, Row } from '../Layout';
import { useSheetStack } from '../OverlayContext/useSheetStack';
import { Typography } from '../Typography';
import styles from './TableList.module.scss';
import { actionColumnDef, expandIconStyle, useTableListActions, useTableListSelector } from './TableList.utils';
import { TableListItem } from './TableListItem';
let idCounter = 0;
const getID = ()=>{
    const id = (idCounter + 1) % (Number.MAX_SAFE_INTEGER - 1);
    idCounter = id;
    return id;
};
const TableListRowContext = /*#__PURE__*/ React.createContext({
    setForceHover () {},
    size: 'medium'
});
export const useTableListRowContext = ()=>useContext(TableListRowContext);
export const TableListRowTestIds = {
    ToggleExpand: (testid)=>"".concat(testid, "-toggle-expand"),
    MobileExpand: (testid)=>"".concat(testid, "-mobile-expand"),
    MobileInline: (testid, index)=>"".concat(testid, "-mobile-inline-").concat(index),
    MobileSecondary: (testid, index)=>"".concat(testid, "-mobile-secondary-").concat(index)
};
const isItemValuePresent = (value)=>value != null && value !== false && value !== '';
const MobileLabel = (param)=>{
    let { label } = param;
    const { text, hideColon } = label, typographyProps = _object_without_properties(label, [
        "text",
        "hideColon"
    ]);
    return /*#__PURE__*/ _jsxs(Typography, _object_spread_props(_object_spread({
        skin: "secondary"
    }, typographyProps), {
        className: styles.mobileCardRowLabel,
        children: [
            text,
            !hideColon && ':'
        ]
    }));
};
const TableListRowInner = /*#__PURE__*/ React.forwardRef(function TableListRow(props, ref) {
    const { as, className, items, expandedContent, expandedContentClassName, rowId, addPulse, onClick, size = 'medium', actionColumn, disableShadow, disableMarginBottom, intent, highlightOnHover, toggleExpandProps = {}, testId, mobile } = props, rest = _object_without_properties(props, [
        "as",
        "className",
        "items",
        "expandedContent",
        "expandedContentClassName",
        "rowId",
        "addPulse",
        "onClick",
        "size",
        "actionColumn",
        "disableShadow",
        "disableMarginBottom",
        "intent",
        "highlightOnHover",
        "toggleExpandProps",
        "testId",
        "mobile"
    ]);
    const { columnDefs, expandableRows, expandableRowsVariant = 'default', enableRowSelection, variant = 'default', hPadding, hasActionColumn, justifyBetween, disableMobile } = useTableListSelector((s)=>s.config);
    const { setSelectedRows, toggleExpandedRow, registerRow, unregisterRow } = useTableListActions();
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const { pushBottomSheetPage } = useSheetStack();
    const isMobileMode = (isMobileDevice || isInBottomSheet) && !disableMobile;
    const [rowEl, setRowEl] = useState(null);
    const expandedContentRef = useRef(null);
    const { getDisabled } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const [forceHover, setForceHover] = useState(false);
    const id = useMemo(()=>rowId !== undefined ? rowId : getID(), [
        rowId
    ]);
    const isExpanded = useTableListSelector((s)=>s.expandedRows[id] || false);
    const isSelected = useTableListSelector((s)=>s.selectedRows[id] || false);
    const RowElement = as || 'div';
    const contextValue = useMemo(()=>({
            setForceHover,
            size
        }), [
        size
    ]);
    const isExpandableDefaultVariant = expandableRows && expandableRowsVariant === 'default';
    const isExpandableClickableIconAndRowVariant = expandableRows && expandableRowsVariant === 'clickableIconAndRow';
    const isExpandableClickableIconVariant = expandableRows && (expandableRowsVariant === 'clickableIcon' || isExpandableClickableIconAndRowVariant);
    useEffect(()=>{
        if (toggleExpandProps.disabled) return;
        registerRow({
            rowId: id,
            isExpanded
        });
        return ()=>{
            unregisterRow(id);
        };
    }, [
        toggleExpandProps.disabled
    ]);
    const resizeObs = useMemo(()=>{
        return new ResizeObserver((entries)=>{
            if (!Array.isArray(entries) || !entries.length) return;
            for (const entry of entries){
                requestAnimationFrame(()=>{
                    if (!entry.target || !expandedContentRef.current) return;
                    expandedContentRef.current.style.width = "".concat(entry.target.scrollWidth, "px");
                });
            }
        });
    }, []);
    useEffect(()=>{
        if (rowEl && isExpanded) {
            resizeObs.observe(rowEl);
            return ()=>resizeObs.unobserve(rowEl);
        }
    }, [
        rowEl,
        resizeObs,
        isExpanded
    ]);
    const stickyItems = useMemo(()=>items.filter((_, idx)=>{
            var _columnDefs_idx;
            return (_columnDefs_idx = columnDefs[idx]) === null || _columnDefs_idx === void 0 ? void 0 : _columnDefs_idx.sticky;
        }), [
        items,
        columnDefs
    ]);
    const stickyColumnDefs = useMemo(()=>columnDefs.filter((col)=>col.sticky), [
        columnDefs
    ]);
    const nonStickyItems = useMemo(()=>items.filter((_, idx)=>{
            var _columnDefs_idx;
            return !((_columnDefs_idx = columnDefs[idx]) === null || _columnDefs_idx === void 0 ? void 0 : _columnDefs_idx.sticky);
        }), [
        items,
        columnDefs
    ]);
    const nonStickyColumnDefs = useMemo(()=>columnDefs.filter((col)=>!col.sticky), [
        columnDefs
    ]);
    if (isMobileMode) {
        const hasMobileExpandAction = expandableRows && !!expandedContent;
        const visibleMobileItems = items.map((item, idx)=>({
                item,
                columnDef: columnDefs[idx]
            })).filter((param)=>{
            let { columnDef } = param;
            return (columnDef === null || columnDef === void 0 ? void 0 : columnDef.mobile) != null && !columnDef.mobile.hide;
        }).filter((param)=>{
            let { item, columnDef } = param;
            var _item_mobileValue;
            var _columnDef_mobile;
            return ((_columnDef_mobile = columnDef.mobile) === null || _columnDef_mobile === void 0 ? void 0 : _columnDef_mobile.showWhenEmpty) || isItemValuePresent((_item_mobileValue = item.mobileValue) !== null && _item_mobileValue !== void 0 ? _item_mobileValue : item.value);
        });
        const isInlineItem = (param, visibleIdx)=>{
            let { columnDef } = param;
            var _columnDef_mobile;
            return visibleIdx > 0 && !!((_columnDef_mobile = columnDef.mobile) === null || _columnDef_mobile === void 0 ? void 0 : _columnDef_mobile.inline);
        };
        const inlineItems = visibleMobileItems.filter(isInlineItem);
        const mobileItems = visibleMobileItems.filter((entry, visibleIdx)=>!isInlineItem(entry, visibleIdx));
        return /*#__PURE__*/ _jsx(TableListRowContext.Provider, {
            value: contextValue,
            children: /*#__PURE__*/ _jsx(Card, {
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: non-actionable Card; rendered as a div with no onClick, so it never emits BI (Card only logs when as==='button').
                noBi: true,
                autoHeight: true,
                padding: "s",
                skin: mobile === null || mobile === void 0 ? void 0 : mobile.skin,
                borderRadius: "m",
                borderSkin: "default",
                testId: testId,
                className: cn(styles.mobileCard, className),
                disableSeparator: true,
                children: /*#__PURE__*/ _jsx("div", {
                    children: mobileItems.map((param, idx)=>{
                        let { item, columnDef } = param;
                        var _item_mobileValue;
                        var _columnDef_mobile, _columnDef_mobile1, _columnDef_mobile_label, _columnDef_mobile2, _columnDef_mobile3;
                        return /*#__PURE__*/ _jsxs(React.Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(ConditionalWrapper, {
                                    condition: idx === 0 && !!(hasActionColumn || hasMobileExpandAction),
                                    wrapper: (children)=>/*#__PURE__*/ _jsxs(FullRow, {
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: styles.mobileRowBeforeActions,
                                                    children: children
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: styles.mobileActions,
                                                    children: [
                                                        hasActionColumn && actionColumn,
                                                        hasMobileExpandAction && /*#__PURE__*/ _jsx(IconButton, {
                                                            // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: TableList owns the inherent fixed-semantic "expand row" action; consumers cannot rename it (rule 6a), so the name is hardcoded by design, not a missing pass-through.
                                                            bi: {
                                                                name: 'expand_row'
                                                            },
                                                            noPadding: true,
                                                            size: "small",
                                                            testId: testId && TableListRowTestIds.MobileExpand(testId),
                                                            onClick: ()=>pushBottomSheetPage({
                                                                    content: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                                                                        header: (mobile === null || mobile === void 0 ? void 0 : mobile.expandedContent) ? {
                                                                            title: mobile.expandedContent.title,
                                                                            subtitle: mobile.expandedContent.subtitle,
                                                                            paddingBottom: 16
                                                                        } : undefined,
                                                                        children: expandedContent()
                                                                    })
                                                                }),
                                                            children: /*#__PURE__*/ _jsx(ChevronRight, {
                                                                size: 16
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                    children: /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            ((_columnDef_mobile = columnDef.mobile) === null || _columnDef_mobile === void 0 ? void 0 : _columnDef_mobile.divider) && /*#__PURE__*/ _jsx("div", {
                                                "data-testid": testId && "".concat(testId, "-mobile-divider-").concat(idx),
                                                children: /*#__PURE__*/ _jsx(Divider, {
                                                    margin: 4,
                                                    skin: (mobile === null || mobile === void 0 ? void 0 : mobile.skin) === 'mobileGray' ? 'white' : undefined
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: cn(styles.mobileCardRow, {
                                                    [styles.spaceBetween]: (_columnDef_mobile1 = columnDef.mobile) === null || _columnDef_mobile1 === void 0 ? void 0 : _columnDef_mobile1.spaceBetween
                                                }),
                                                "data-testid": testId && "".concat(testId, "-mobile-row-").concat(idx),
                                                children: [
                                                    ((_columnDef_mobile2 = columnDef.mobile) === null || _columnDef_mobile2 === void 0 ? void 0 : (_columnDef_mobile_label = _columnDef_mobile2.label) === null || _columnDef_mobile_label === void 0 ? void 0 : _columnDef_mobile_label.text) && /*#__PURE__*/ _jsx(MobileLabel, {
                                                        label: columnDef.mobile.label
                                                    }),
                                                    /*#__PURE__*/ _jsx(Typography, _object_spread_props(_object_spread({
                                                        skin: "secondary"
                                                    }, (_columnDef_mobile3 = columnDef.mobile) === null || _columnDef_mobile3 === void 0 ? void 0 : _columnDef_mobile3.value), {
                                                        className: cn(styles.mobileCardRowValue, {
                                                            [styles.withInlineItems]: idx === 0 && inlineItems.length > 0
                                                        }),
                                                        children: (_item_mobileValue = item.mobileValue) !== null && _item_mobileValue !== void 0 ? _item_mobileValue : item.value
                                                    })),
                                                    idx === 0 && inlineItems.map((param, inlineIdx)=>{
                                                        let { item: inlineItem } = param;
                                                        return /*#__PURE__*/ _jsx(Row, {
                                                            alignItems: "center",
                                                            className: styles.mobileCardRowInline,
                                                            testId: testId ? TableListRowTestIds.MobileInline(testId, inlineIdx) : undefined,
                                                            children: inlineItem.value
                                                        }, inlineIdx);
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                }),
                                isItemValuePresent(item.mobileSecondaryValue) && /*#__PURE__*/ _jsx("div", {
                                    className: styles.mobileCardRow,
                                    "data-testid": testId && TableListRowTestIds.MobileSecondary(testId, idx),
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        skin: "secondary",
                                        className: styles.mobileCardRowValue,
                                        children: item.mobileSecondaryValue
                                    })
                                })
                            ]
                        }, idx);
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsxs(TableListRowContext.Provider, {
        value: contextValue,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                ref: setRowEl,
                className: cn(styles.row, styles["".concat(variant, "RowVariant")], styles["".concat(size, "RowSize")], {
                    [styles.hover]: isExpanded && isExpandableDefaultVariant || forceHover,
                    [styles.selected]: isSelected,
                    [styles.pulse]: addPulse,
                    [styles.disableShadow]: disableShadow,
                    [styles.disableMarginBottom]: disableMarginBottom,
                    [styles.highlightOnHover]: highlightOnHover
                }),
                children: [
                    /*#__PURE__*/ _jsxs(RowElement, _object_spread_props(_object_spread({}, rest, testId != null ? {
                        'data-testid': testId
                    } : {}), {
                        ref: ref,
                        "data-id": id,
                        "data-checked": isSelected,
                        disabled: disabledFromIntent,
                        className: cn(styles.rowInner, styles["".concat(variant, "RowInnerVariant")], styles["".concat(size, "RowSize")], styles["".concat(hPadding, "Padding")], className, {
                            [styles.clickable]: onClick,
                            [styles.justifyBetween]: justifyBetween
                        }),
                        onClick: (event)=>{
                            (isExpandableDefaultVariant || isExpandableClickableIconAndRowVariant) && !toggleExpandProps.disabled && toggleExpandedRow(id);
                            onClick && onClick(event);
                        },
                        children: [
                            (enableRowSelection || isExpandableClickableIconVariant || stickyItems.length > 0) && /*#__PURE__*/ _jsxs("div", {
                                className: cn(styles.stickyCheckboxWrapper, styles["".concat(hPadding, "Padding")]),
                                children: [
                                    (enableRowSelection || isExpandableClickableIconVariant) && /*#__PURE__*/ _jsxs("div", {
                                        className: cn(styles.stickyCheckbox, styles["".concat(hPadding, "Padding")]),
                                        onClick: (e)=>e.stopPropagation(),
                                        children: [
                                            enableRowSelection && /*#__PURE__*/ _jsx(Checkbox, {
                                                testId: testId && "".concat(testId, "-checkbox"),
                                                checked: isSelected,
                                                onChange: (e)=>{
                                                    setSelectedRows({
                                                        [id]: e.target.checked
                                                    });
                                                    // This prevents TableListActions to be visible if we click and select a row.
                                                    if (document.body.classList.contains(FOCUS_DISABLED_CLASS)) {
                                                        e.target.blur();
                                                    }
                                                },
                                                // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: TableList owns the inherent fixed-semantic "select row" action; checked maps directly to row selection so on/off match the visible affordance (rule 6a), name hardcoded by design.
                                                bi: {
                                                    name: 'select_row'
                                                }
                                            }),
                                            isExpandableClickableIconVariant && /*#__PURE__*/ _jsx(IconButton, {
                                                // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: TableList owns the inherent fixed-semantic "toggle expand row" action; consumers cannot rename it (rule 6a), so the name is hardcoded by design, not a missing pass-through.
                                                bi: {
                                                    name: 'toggle_expand'
                                                },
                                                size: "small",
                                                noPadding: true,
                                                testId: testId && TableListRowTestIds.ToggleExpand(testId),
                                                disabled: toggleExpandProps.disabled,
                                                tooltip: toggleExpandProps.tooltipText ? {
                                                    title: toggleExpandProps.tooltipText,
                                                    placement: 'right'
                                                } : undefined,
                                                onClick: ()=>toggleExpandedRow(id),
                                                children: isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {
                                                    size: 20
                                                }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                                    size: 20
                                                })
                                            })
                                        ]
                                    }),
                                    stickyItems.length > 0 && /*#__PURE__*/ _jsx("div", {
                                        className: cn(styles.rowItemData, justifyBetween && styles.justifyBetween),
                                        children: stickyItems.map((item, idx)=>/*#__PURE__*/ _jsx(TableListItem, {
                                                columnDef: stickyColumnDefs[idx],
                                                item: item
                                            }, idx))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.rowItemData, justifyBetween && styles.justifyBetween),
                                children: nonStickyItems.map((item, idx)=>/*#__PURE__*/ _jsx(TableListItem, {
                                        columnDef: nonStickyColumnDefs[idx],
                                        item: item
                                    }, idx))
                            }),
                            (hasActionColumn || isExpandableDefaultVariant) && /*#__PURE__*/ _jsxs("div", {
                                className: cn(styles.rowActionSticky, styles["".concat(hPadding, "Padding")]),
                                children: [
                                    hasActionColumn && /*#__PURE__*/ _jsx(TableListItem, {
                                        columnDef: actionColumnDef,
                                        item: {
                                            value: actionColumn || ''
                                        }
                                    }),
                                    isExpandableDefaultVariant && /*#__PURE__*/ _jsx("div", {
                                        style: expandIconStyle,
                                        children: isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronRight, {})
                                    })
                                ]
                            })
                        ]
                    })),
                    isExpanded && isExpandableDefaultVariant && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.separator
                            }),
                            expandedContent && expandedContent()
                        ]
                    })
                ]
            }),
            isExpanded && isExpandableClickableIconVariant && expandedContent && /*#__PURE__*/ _jsx("div", {
                ref: expandedContentRef,
                className: cn(styles.expandedContent, expandedContentClassName),
                children: expandedContent()
            })
        ]
    });
});
TableListRowInner.displayName = 'TableListRow';
const TableListRow = /*#__PURE__*/ React.memo(TableListRowInner);
export { TableListRow };
